// Shared Mapbox bootstrap — single source of truth for the access token + map creation,
// imported by BOTH the SA interactive-map and the TDU stage map so there is not a third
// drifting map impl — same intent in N places (plan v2 §5/§9).
//
// mapbox-gl itself is loaded from the CDN <script> in each block's .cshtml and read off
// window.mapboxgl (matches interactive-map). Only the token + thin helpers live here.
//
// ⚠️ TOKEN: this is the existing personal token, in use temporarily. Replace with the
// SATC-owned, URL-restricted token before launch (plan v2 §10). Changing it here updates
// every map at once.
export const MAPBOX_TOKEN = 'pk.eyJ1IjoiY2FsZWJnbnoiLCJhIjoiY2pudHBncjUzMHdlejNrcGF0c2FwdDJmeiJ9.g13YwdYY0xdF2InOImcbjA';

export const MAPBOX_GL_VERSION = 'v3.4.0';

/** Returns the CDN-loaded mapbox-gl global with the token applied. Throws if the CDN script is absent. */
export function getMapboxGl() {
    const gl = typeof window !== 'undefined' ? window.mapboxgl : undefined;
    if (!gl) throw new Error('mapbox-gl not found on window — the CDN <script> in the block .cshtml must load before this bundle.');
    gl.accessToken = MAPBOX_TOKEN;
    return gl;
}

/** Create a Mapbox map on `container` with sensible TDU defaults overridable via opts. */
export function createMap(container, opts = {}) {
    const mapboxgl = getMapboxGl();
    return new mapboxgl.Map({ container, ...opts });
}

/**
 * Toggle the monochrome base on Mapbox Standard (plan §5). Safe no-op on styles that don't
 * support config properties. Call after the style has loaded.
 */
export function applyMonochrome(map, on = true) {
    try {
        map.setConfigProperty('basemap', 'theme', on ? 'monochrome' : 'default');
        return true;
    } catch (e) {
        // Surface it — swallowing this is exactly why monochrome silently failed before.
        console.warn('[tdu-stage-map] monochrome theme not applied:', e && e.message);
        return false;
    }
}

/**
 * Flatten the Mapbox Standard base to a 2D top-down look by dropping 3D buildings/landmarks, so
 * an overhead route reads cleanly. Safe no-op on styles/versions without the config property
 * (the camera pitch lock — pitch/maxPitch:0 — is set via Map options at creation, independent of
 * this). Call after the style has loaded ('style.load').
 */
export function applyFlat2D(map) {
    try {
        map.setConfigProperty('basemap', 'show3dObjects', false);
        return true;
    } catch (e) {
        console.warn('[mapbox] flat 2D base not applied:', e && e.message);
        return false;
    }
}

/**
 * Set the Mapbox Standard day/night lighting preset ('day' | 'dawn' | 'dusk' | 'night').
 * 'night' gives a dark base (keeping coloured roads) so a bright route line reads boldly —
 * matching the look of the SA related-content map. Safe no-op on styles without the config
 * property. Call after the style has loaded ('style.load'). Shared so the SA map can reuse it
 * rather than spawning a third drifting impl (same intent in N places).
 */
export function applyLightPreset(map, preset = 'night') {
    try {
        map.setConfigProperty('basemap', 'lightPreset', preset);
        return true;
    } catch (e) {
        console.warn('[mapbox] lightPreset not applied:', e && e.message);
        return false;
    }
}
